import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const NewCampaign = ({ currentUserId, appMode }) => {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [formData, setFormData] = useState({
    listingId: '',
    title: '',
    platform: '',
    budget: '',
    minFollowers: '',
    duration: 'week'
  });

  useEffect(() => {
    if (appMode !== 'Agent' && appMode !== 'Private Owner') {
      navigate('/marketplace');
      return;
    }
    fetchListings();
  }, [currentUserId, appMode]);

  const fetchListings = async () => {
    try {
      const response = await axios.get(`${API}/listings/owner/${currentUserId}`);
      setListings(response.data);
    } catch (error) {
      console.error('Error fetching listings:', error);
      toast.error('Failed to load your listings');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // When listing is selected, store the full listing object for preview
    if (field === 'listingId') {
      const listing = listings.find(l => l.id === value);
      setSelectedListing(listing);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.listingId || !formData.title || !formData.platform || !formData.budget) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const campaignData = {
        listingId: formData.listingId,
        title: formData.title,
        platform: formData.platform,
        budget: parseFloat(formData.budget),
        minFollowers: parseInt(formData.minFollowers) || 0,
        duration: formData.duration,
        ownerId: currentUserId
      };

      await axios.post(`${API}/campaigns`, campaignData);
      toast.success('Campaign launched successfully!');
      
      setFormData({
        listingId: '',
        title: '',
        platform: '',
        budget: '',
        minFollowers: '',
        duration: 'week'
      });
      
      navigate('/dashboard');
    } catch (error) {
      console.error('Error launching campaign:', error);
      toast.error('Failed to launch campaign');
    }
  };

  if (appMode !== 'Agent' && appMode !== 'Private Owner') {
    return null;
  }

  return (
    <main className="max-w-7xl mx-auto px-4 py-8">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl" style={{ fontFamily: 'Playfair Display, serif' }}>Launch Influencer Campaign</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="listingId">Select Listing to Promote *</Label>
              <Select value={formData.listingId} onValueChange={(value) => handleInputChange('listingId', value)} required>
                <SelectTrigger id="listingId" data-testid="campaign-listing-select">
                  <SelectValue placeholder="Choose a listing" />
                </SelectTrigger>
                <SelectContent>
                  {listings.length === 0 ? (
                    <SelectItem value="none" disabled>No listings available</SelectItem>
                  ) : (
                    listings.map(listing => (
                      <SelectItem key={listing.id} value={listing.id}>{listing.title}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Listing Preview Card */}
            {selectedListing && (
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-lg p-4">
                <h3 className="font-bold text-purple-800 mb-3 flex items-center gap-2">
                  <i className="fas fa-eye"></i> Property Preview (Influencers will see this)
                </h3>
                <div className="bg-white rounded-lg overflow-hidden shadow-md flex gap-4">
                  {/* Property Image */}
                  <div className="w-48 h-32 flex-shrink-0">
                    {selectedListing.images && selectedListing.images.length > 0 ? (
                      <img 
                        src={selectedListing.images[0]} 
                        alt={selectedListing.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <span className="text-4xl">🏠</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Property Details */}
                  <div className="flex-1 py-2 pr-4">
                    <h4 className="font-bold text-lg mb-1">{selectedListing.title}</h4>
                    <p className="text-blue-600 font-bold text-xl mb-2">
                      R{selectedListing.price?.toLocaleString()}
                    </p>
                    <div className="flex gap-4 text-sm text-gray-600">
                      <span><i className="fas fa-bed mr-1"></i>{selectedListing.specifications?.beds || 0} Beds</span>
                      <span><i className="fas fa-bath mr-1"></i>{selectedListing.specifications?.baths || 0} Baths</span>
                      <span><i className="fas fa-home mr-1"></i>{selectedListing.propertyType}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {selectedListing.type === 'sale' ? 'For Sale' : 'For Rent'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="title">Campaign Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="e.g., 'Modern Living' Promotion"
                required
                data-testid="campaign-title-input"
              />
            </div>

            <div>
              <Label htmlFor="platform">Target Platform *</Label>
              <Select value={formData.platform} onValueChange={(value) => handleInputChange('platform', value)} required>
                <SelectTrigger id="platform" data-testid="campaign-platform-select">
                  <SelectValue placeholder="Select platform" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Instagram">Instagram</SelectItem>
                  <SelectItem value="TikTok">TikTok</SelectItem>
                  <SelectItem value="Facebook">Facebook</SelectItem>
                  <SelectItem value="X">X (Twitter)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="budget">Total Budget (ZAR) *</Label>
              <Input
                id="budget"
                type="number"
                value={formData.budget}
                onChange={(e) => handleInputChange('budget', e.target.value)}
                placeholder="50000"
                required
                data-testid="campaign-budget-input"
              />
            </div>

            <div>
              <Label htmlFor="minFollowers">Minimum Followers Required</Label>
              <Input
                id="minFollowers"
                type="number"
                value={formData.minFollowers}
                onChange={(e) => handleInputChange('minFollowers', e.target.value)}
                placeholder="10000"
                data-testid="campaign-min-followers-input"
              />
            </div>

            <div>
              <Label htmlFor="duration">Campaign Duration *</Label>
              <Select value={formData.duration} onValueChange={(value) => handleInputChange('duration', value)} required>
                <SelectTrigger id="duration" data-testid="campaign-duration-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">1 Day</SelectItem>
                  <SelectItem value="week">1 Week</SelectItem>
                  <SelectItem value="month">1 Month</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-4 pt-4">
              <Button type="submit" className="bg-red-600 hover:bg-red-700" data-testid="launch-campaign-btn">
                <i className="fas fa-rocket mr-2"></i> Launch Campaign
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/dashboard')}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </main>
  );
};

export default NewCampaign;